/**
 * Named moments the page announces for whoever plays something on them (ADR-073's amendment "the respawn is the
 * player's", decision 8): the sound code subscribes to a cue by its name and the page fires it, so neither imports the
 * other. A cue carries nothing but its name; a listener that throws is reported and never stops the others.
 *
 * @example
 * ```ts
 * const stop = PAGE_CUES.on("player_death", () => play("stingers/player_death"));
 * PAGE_CUES.fire("player_death");
 * stop();
 * ```
 */

/**
 * The cues the page fires: `player_death` as the death screen appears for a death seen live.
 */
export type CueName = "player_death";

/**
 * The page's cues: listeners by name, and how many times each was fired, which the report reads.
 */
export class Cues {
  private readonly listeners = new Map<CueName, Set<() => void>>();
  private readonly counts = new Map<CueName, number>();

  /**
   * Listens for a cue.
   *
   * @param name The cue.
   * @param listener Called each time it fires.
   * @returns A function that stops listening.
   */
  public on(name: CueName, listener: () => void): () => void {
    const set = this.listeners.get(name) ?? new Set<() => void>();
    set.add(listener);
    this.listeners.set(name, set);
    return () => set.delete(listener);
  }

  /**
   * Fires a cue: counts it, then calls every listener in the order they listened.
   *
   * @param name The cue.
   */
  public fire(name: CueName): void {
    this.counts.set(name, (this.counts.get(name) ?? 0) + 1);
    for (const listener of this.listeners.get(name) ?? []) {
      try {
        listener();
      } catch (error) {
        console.error(`a listener for the cue ${name} failed`, error);
      }
    }
  }

  /**
   * How many times a cue has fired on this page.
   *
   * @param name The cue.
   * @returns The count.
   */
  public count(name: CueName): number {
    return this.counts.get(name) ?? 0;
  }
}

/**
 * The page's one set of cues.
 */
export const PAGE_CUES = new Cues();
